import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '@hooks/useAuth';

/**
 * ProtectedRoute component - wraps routes that require authentication
 * @param {ReactNode} children - Child components to render if authorized
 * @param {Array<string>} roles - Allowed user roles (optional)
 * @param {string} organizationId - Required organization ID (optional)
 */
const ProtectedRoute = ({ children, roles = null, organizationId = null }) => {
  const { currentUser, userData, loading, isAuthenticated, hasRole, belongsToOrganization } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated()) {
    // Redirect to login page, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (roles && !hasRole(roles)) {
    // Redirect to dashboard if user doesn't have required role
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user belongs to required organization
  if (organizationId && !belongsToOrganization(organizationId)) {
    // Redirect to dashboard if user doesn't belong to required organization
    return <Navigate to="/dashboard" replace />;
  }

  // User is authorized, render the protected content
  return children;
};

export default ProtectedRoute;
